import {useId} from 'react';
import {useSelector} from 'react-redux';

import ButtonRaw from '@/components/Buttons/ButtonRaw';
import {BADGE_TRACKING} from '@/consts/badgeTracking';
import useBadgeDetailRoute from '@/hooks/useBadgeDetailRoute';
import {trackBadgeClicked, trackBadgesInProgressClicked} from '@/services/events/loyaltyEventTracking';
import appRouterService from '@/services/route/appRouterService';
import {selectInProgressBadges} from '@/state/selectors/loyalty';
import type {BadgesInProgressSectionContentModel} from '@/types/aem';

import BadgeInProgressRow from './BadgeInProgressRow';
import BadgeStack from './BadgeStack';

type BadgesInProgressSectionProps = {
    content?: BadgesInProgressSectionContentModel | null;
};

const BadgesInProgressSection = ({content}: BadgesInProgressSectionProps) => {
    const titleId = useId();
    const {openBadge} = useBadgeDetailRoute();
    const {title, showAllButton} = content ?? {};
    const inProgressBadges = useSelector(selectInProgressBadges);
    const previewBadge = inProgressBadges[0];

    if (!previewBadge) return null;

    const hasMultipleBadges = inProgressBadges.length > 1;
    const stackBadges = inProgressBadges.slice(1, 4);
    const handleClick = () => {
        if (!hasMultipleBadges) {
            trackBadgeClicked(previewBadge.title, BADGE_TRACKING.PAGE_IDS.BADGES);
            openBadge(previewBadge);
            return;
        }

        trackBadgesInProgressClicked();
        appRouterService.forwardToInProgressPage();
    };

    return (
        <div className='px-4'>
            <ButtonRaw
                aria-labelledby={title ? titleId : undefined}
                data-testid='badges-in-progress-section-button'
                onClick={handleClick}
                className='flex w-full flex-col gap-4 rounded-xl border-0 bg-secondary p-4 text-left text-romance outline-none focus-visible:ring-2 focus-visible:ring-white/40'
            >
                {title && (
                    <h2 id={titleId} className='m-0 font-bold text-20 text-romance leading-14'>
                        {title}
                    </h2>
                )}
                <div className='flex items-center gap-4'>
                    <BadgeInProgressRow badge={previewBadge} className='flex-1' />
                    {hasMultipleBadges && (
                        <div className='flex flex-col items-center gap-0'>
                            {stackBadges.length > 0 && <BadgeStack badges={stackBadges} />}
                            {showAllButton?.text && (
                                <span className='font-bold text-14 text-romance leading-143'>{showAllButton.text}</span>
                            )}
                        </div>
                    )}
                </div>
            </ButtonRaw>
        </div>
    );
};

export default BadgesInProgressSection;
